"use client";

import * as Sentry from "@sentry/nextjs";
import type { AbstractIntlMessages } from "next-intl";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { LOCALE_KEY } from "@/constants/app.constants";
import type { Locale } from "@/i18n/config";
import { resolveStoredLocale } from "@/providers/LocaleProvider";
import enMessages from "@/locales/en/common.json";
import esMessages from "@/locales/es/common.json";
const globalErrorMessagesSchema = z.object({
  title: z.string(),
  description: z.string(),
  referenceId: z.string(),
  tryAgain: z.string()
});
type GlobalErrorMessages = z.infer<typeof globalErrorMessagesSchema>;
const HARDCODED_LAST_RESORT: GlobalErrorMessages = {
  title: "The application has encountered an error.",
  description: "Our team has been notified and will investigate the issue.",
  referenceId: "Reference ID: {digest}",
  tryAgain: "Try again"
};
function readGlobalErrorMessages(messages: AbstractIntlMessages, fallback: GlobalErrorMessages): GlobalErrorMessages {
  const errors = messages.errors;
  if (typeof errors === "string") return fallback;
  const parsed = globalErrorMessagesSchema.safeParse(errors.global);
  return parsed.success ? parsed.data : fallback;
}
const EN_GLOBAL_ERROR_MESSAGES = readGlobalErrorMessages(enMessages, HARDCODED_LAST_RESORT);
const GLOBAL_ERROR_MESSAGES: Record<Locale, GlobalErrorMessages> = {
  en: EN_GLOBAL_ERROR_MESSAGES,
  es: readGlobalErrorMessages(esMessages, EN_GLOBAL_ERROR_MESSAGES)
};
export default function GlobalError({
  error,
  reset
}: {
  error: Error & {
    digest?: string;
  };
  reset: () => void;
}) {
  const [locale, setLocale] = useState<Locale>("en");
  useEffect(() => {
    Sentry.captureException(error);
    try {
      setLocale(resolveStoredLocale(localStorage.getItem(LOCALE_KEY)));
    } catch {
      // localStorage can be unavailable (private browsing, disabled) —
      // this is the global error boundary, so it falls back to "en"
      // rather than risk throwing again.
    }
  }, [error]);
  const t = GLOBAL_ERROR_MESSAGES[locale];
  return <html data-sentry-component="GlobalError" data-sentry-source-file="global-error.tsx">
            <body>
                <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 p-4">
                    <div className="text-center space-y-4">
                        <h2 className="text-2xl font-bold">{t.title}</h2>
                        <p className="text-muted-foreground">{t.description}</p>
                        {error.digest && <p className="text-sm text-muted-foreground">
                                {t.referenceId.replace("{digest}", error.digest)}
                            </p>}
                        <Button onClick={() => reset()} data-sentry-element="Button" data-sentry-source-file="global-error.tsx">{t.tryAgain}</Button>
                    </div>
                </div>
            </body>
        </html>;
}